import type { TFunction } from 'i18next'
import { Check, CircleAlert, LoaderCircle, type LucideIcon, MicOff } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Progress } from '@/components/ui/progress'
import { cn } from '@/utils/cn'
import { formatPercent } from '../format'
import { maySendAgain } from '../questioning/recording-types'
import { CALLOUT_WARNING, MONO, TEXT_CAPTION } from '../recipes'
import { VoiceSampleVerdict } from './VoiceSampleVerdict'
import type { VoiceSampleFailure, VoiceSampleView } from './voice-types'

const COPY = 'humint.voice.sample'

/** What is happening to a sample on its way: it is prepared, then sent. */
export const savingText = (progress: number | null, t: TFunction): string =>
  t(progress === null ? `${COPY}.saving.hashing` : `${COPY}.saving.uploading`)

/** The sample is hashed and sent: what is happening, how far it is, and that the window stays open. */
function Saving({ progress }: { progress: number | null }) {
  const { t, i18n } = useTranslation()
  const label = savingText(progress, t)
  return (
    <div className="flex w-full flex-col gap-3">
      <p className="flex items-center gap-3 text-base font-semibold">
        <LoaderCircle
          aria-hidden
          className="size-5 shrink-0 animate-spin text-primary motion-reduce:animate-none"
        />
        <span className="min-w-0 flex-1">{label}</span>
        {progress !== null && (
          <span className={cn(MONO, 'text-[13px] font-normal text-muted-foreground')}>
            {formatPercent(progress, i18n.language)}
          </span>
        )}
      </p>
      <Progress
        value={progress === null ? 0 : progress * 100}
        aria-label={label}
        className={cn(
          'h-1.5 bg-accent',
          progress === null && 'animate-pulse motion-reduce:animate-none'
        )}
      />
      <p className={cn(TEXT_CAPTION, 'text-muted-foreground')}>{t(`${COPY}.saving.keepOpen`)}</p>
    </div>
  )
}

interface OutcomeLook {
  Icon: LucideIcon
  /** The icon's tone: never the only thing that says the state. */
  tone: string
  spin?: boolean
  /**
   * Something went wrong: said at once to a screen reader, as an alert. The other outcomes are
   * told through the instrument's status line.
   */
  alert?: boolean
}

const OUTCOME = {
  processing: { Icon: LoaderCircle, tone: 'text-primary', spin: true },
  done: { Icon: Check, tone: 'text-primary-strong' },
  noSpeech: { Icon: MicOff, tone: 'text-warning-foreground', alert: true },
  processingFailed: { Icon: CircleAlert, tone: 'text-destructive', alert: true },
} satisfies Record<string, OutcomeLook>

/** How a stored sample stands: an icon, what it is called, and what it means for the operator. */
function Outcome({ kind }: { kind: keyof typeof OUTCOME }) {
  const { t } = useTranslation()
  const { Icon, tone, spin, alert }: OutcomeLook = OUTCOME[kind]
  return (
    <div
      role={alert ? 'alert' : undefined}
      className="flex w-full flex-col items-center gap-1.5 text-center"
    >
      <p className="flex items-center gap-2 text-base font-semibold">
        <Icon
          aria-hidden
          className={cn('size-5 shrink-0', tone, spin && 'animate-spin motion-reduce:animate-none')}
        />
        {t(`${COPY}.outcome.${kind}.title`)}
      </p>
      <p className={cn(TEXT_CAPTION, 'max-w-[38ch] text-balance text-muted-foreground')}>
        {t(`${COPY}.outcome.${kind}.body`)}
      </p>
    </div>
  )
}

/** A sample that could not be sent: not saved yet (it may go again), or refused for good. */
function SendFailed({ failure }: { failure: Extract<VoiceSampleFailure, { kind: 'send' }> }) {
  const { t } = useTranslation()
  const again = maySendAgain(failure.problem)
  return (
    <div role="alert" className={cn(CALLOUT_WARNING, 'w-full font-normal')}>
      <CircleAlert aria-hidden className="mt-px size-4 shrink-0" />
      <span className="flex min-w-0 flex-col gap-0.5">
        <strong className="font-semibold">
          {t(again ? `${COPY}.failed.notSaved` : `${COPY}.failed.refused`)}
        </strong>
        <span>{t(`${COPY}.failed.problem.${failure.problem}`)}</span>
      </span>
    </div>
  )
}

export type VoiceSampleStatusView = Extract<
  VoiceSampleView,
  { phase: 'saving' | 'processing' | 'done' | 'no_speech' | 'failed' }
>

/**
 * Under the ring once the operator kept the sample (ADR-0062): it is being sent, with its
 * progress; stored, its voiceprint being computed — the window may be closed; the voiceprint is
 * kept with the subject, with the server's verdict on the sample once the page knows it; the
 * server found too little speech in it; or it failed, with why and whether the same sample may
 * go again. Every state is an icon and words, never colour alone.
 */
export function VoiceSampleStatus({ view }: { view: VoiceSampleStatusView }) {
  switch (view.phase) {
    case 'saving':
      return <Saving progress={view.progress} />
    case 'processing':
      return <Outcome kind="processing" />
    case 'done':
      return view.quality ? <VoiceSampleVerdict quality={view.quality} /> : <Outcome kind="done" />
    case 'no_speech':
      return <Outcome kind="noSpeech" />
    case 'failed':
      return view.failure.kind === 'send' ? (
        <SendFailed failure={view.failure} />
      ) : (
        <Outcome kind="processingFailed" />
      )
  }
}
